/*
  Delivery Areas & Coverage - postcode checker, cut-off clock, coverage map.
  Vanilla, no dependencies, no build step. Leaflet is fetched lazily and only
  when the map scrolls into view.

  Two things here are carried verbatim from the design handoff and must not
  drift: the copy in renderResult(), and the cut-off logic in slot().
*/
(function () {
  'use strict';

  /* ---------------------------------------------------------------- clock */
  /* Cut-offs are uniform across the whole footprint and depend on the current
     time and day of week only - never on the postcode.

     Each entry maps a cut-off to what meeting it guarantees: order by 2PM and it
     rides the 1PM-6PM run, so it lands before 6PM; order by 5PM and it rides the
     6PM-9PM run, so it lands before 9PM. Sundays have no evening run, which makes
     2PM the last same-day cut-off that day.

     Keep these in step with the "When it arrives" table in delivery-areas.liquid. */
  var DEADLINES = [
    { cutoff: 14, by: '6PM' },
    { cutoff: 17, by: '9PM', weekdaysOnly: true }
  ];

  /* Once today is out of reach, the next chance is tomorrow's morning run:
     order by 7AM and it lands before 1PM. */
  var TOMORROW = { cutoff: 7, by: '1PM' };

  var DAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /* Always Asia/Kuala_Lumpur, never the visitor's device clock: a KL customer
     on a device set to another timezone must still see the correct cut-off.
     Computed client-side rather than rendered from Liquid because Shopify's
     full-page cache would freeze a server-rendered timestamp. */
  function nowKL() {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kuala_Lumpur',
        weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
      var got = {};
      for (var i = 0; i < parts.length; i++) got[parts[i].type] = parts[i].value;
      var day = DAYS[got.weekday];
      var hour = parseInt(got.hour, 10);
      var minute = parseInt(got.minute, 10);
      if (day === undefined || isNaN(hour) || isNaN(minute)) throw new Error('bad parts');
      if (hour === 24) hour = 0; /* some engines render midnight as 24 */
      return { day: day, mins: hour * 60 + minute };
    } catch (e) {
      /* No IANA timezone support: MYT is a fixed UTC+8 with no DST, so this
         fallback is exact rather than approximate. */
      var d = new Date(Date.now() + 8 * 3600 * 1000);
      return { day: d.getUTCDay(), mins: d.getUTCHours() * 60 + d.getUTCMinutes() };
    }
  }

  /* The soonest cut-off still ahead, and what meeting it guarantees. Before 2PM
     that is "before 6PM today"; between 2PM and 5PM it is "before 9PM today";
     on Sundays the 5PM step is skipped, so after 2PM it rolls to tomorrow. */
  function slot() {
    var t = nowKL();
    for (var i = 0; i < DEADLINES.length; i++) {
      var d = DEADLINES[i];
      if (d.weekdaysOnly && t.day === 0) continue;
      var c = d.cutoff * 60;
      if (t.mins < c) return { by: d.by, left: c - t.mins, today: true };
    }
    return { by: TOMORROW.by, left: (24 * 60 - t.mins) + TOMORROW.cutoff * 60, today: false };
  }

  function humanise(left) {
    var h = Math.floor(left / 60), m = left % 60;
    if (h > 0 && m === 0) return h + (h === 1 ? ' hour' : ' hours');
    if (h > 0) return h + (h === 1 ? ' hour ' : ' hours ') + m + (m === 1 ? ' minute' : ' minutes');
    return m + (m === 1 ? ' minute' : ' minutes');
  }

  /* ------------------------------------------------------------- coverage */
  /* The whitelist ships grouped by area (see snippets/delivery-coverage-data.liquid)
     because 671 postcodes cost ~7KB that way against ~25KB as a flat map. Flattened
     into a lookup table once, here. */
  var coverage = null;

  function loadCoverage() {
    var el = document.getElementById('hbd-coverage');
    if (!el) return;
    var map = {};
    try {
      var data = JSON.parse(el.textContent);
      (data.areas || []).forEach(function (a) {
        (a.codes || []).forEach(function (pc) { map[pc] = a; });
      });
    } catch (e) {
      /* Leave coverage empty rather than half-parsed: every lookup then falls to
         the failure state, which offers pickup instead of a dead end. */
      if (window.console) console.error('[delivery] coverage data failed to parse', e);
    }
    coverage = map;
    if (!Object.keys(map).length && window.console) {
      console.warn('[delivery] coverage whitelist is empty - every postcode will report as not served.');
    }
  }

  function lookup(pc) {
    return (coverage && coverage[pc]) || null;
  }

  /* "Subang Jaya, Selangor", but just "Kuala Lumpur" rather than
     "Kuala Lumpur, Kuala Lumpur". */
  function placeName(match) {
    return match.area === match.district ? match.area : match.area + ', ' + match.district;
  }

  /* -------------------------------------------------------------- checker */
  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return ESCAPES[c]; });
  }

  function initChecker(root) {
    var form = root.querySelector('[data-hbd-form]');
    var input = root.querySelector('[data-hbd-input]');
    var out = root.querySelector('[data-hbd-result]');
    if (!form || !input || !out) return;

    var shopUrl = root.getAttribute('data-shop-url') || '/collections/flowers';
    var state = { pc: '', status: 'idle', match: null };
    var timer = null;

    function renderResult() {
      if (state.status === 'idle') {
        out.innerHTML =
          '<p class="hbd-checker__hint">Free delivery everywhere we go. No zones, no fees, no minimum spend.</p>';
        return;
      }

      if (state.status === 'invalid') {
        out.innerHTML =
          '<div class="hbd-res hbd-res--invalid">' +
            "<p>That doesn't look like a Malaysian postcode - mind checking it? It should be 5 digits.</p>" +
          '</div>';
        return;
      }

      if (state.status === 'success') {
        var s = slot();
        out.innerHTML =
          '<div class="hbd-res hbd-res--ok">' +
            '<div class="hbd-res__body">' +
              '<span class="hbd-res__tick" aria-hidden="true">&#10003;</span>' +
              '<div>' +
                '<p class="hbd-res__lead">Yes - we deliver to ' +
                  esc(placeName(state.match)) + '.</p>' +
                '<p class="hbd-res__sub"><strong class="hbd-res__free">Free delivery.</strong> ' +
                  'Order in the next <strong>' + esc(humanise(s.left)) + '</strong> for delivery before ' +
                  esc(s.by) + ' ' + (s.today ? 'today' : 'tomorrow') + ', or pick any future date.</p>' +
              '</div>' +
            '</div>' +
            '<a class="hbd-res__go" href="' + esc(shopUrl) + '">Start shopping &rarr;</a>' +
          '</div>';
        return;
      }

      /* failure - never a dead end */
      out.innerHTML =
        '<div class="hbd-res hbd-res--no">' +
          '<p class="hbd-res__lead">We don&#39;t deliver to ' + esc(state.pc || 'that postcode') + ' just yet.</p>' +
          '<p class="hbd-res__sub">You can still send a Happy Bunch - collect from our Bandar Sunway workshop ' +
            'yourself, or book a Lalamove / GrabExpress / GoGet rider to pick it up from us and take it the ' +
            'rest of the way.</p>' +
          '<div class="hbd-res__actions">' +
            '<a class="hbd-btn hbd-btn--dark" href="#pickup">How self-pickup works &rarr;</a>' +
            '<a class="hbd-btn hbd-btn--ghost" href="' + esc(shopUrl) + '">Shop anyway &rarr;</a>' +
          '</div>' +
        '</div>';
    }

    /* A success panel left open on an idle tab would otherwise show a stale
       countdown. Cheap to keep honest. */
    function retick() {
      if (timer) clearInterval(timer);
      timer = setInterval(function () {
        if (state.status === 'success') renderResult();
      }, 30000);
    }

    input.addEventListener('input', function () {
      var cleaned = input.value.replace(/[^\d]/g, '').slice(0, 5);
      if (cleaned !== input.value) input.value = cleaned;
      state.pc = cleaned;
      if (state.status !== 'idle') { state.status = 'idle'; state.match = null; renderResult(); }
    });

    // Enter should check the postcode, not just the Check button. The theme cancels
    // the Enter keydown on inputs, which kills the browser's implicit form submission,
    // so trigger the submit path explicitly.
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.keyCode !== 13) return;
      e.preventDefault();
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var pc = String(state.pc || input.value || '').trim();
      if (!/^\d{5}$/.test(pc)) {
        state.status = 'invalid';
        state.match = null;
      } else {
        var hit = lookup(pc);
        state.pc = pc;
        state.status = hit ? 'success' : 'fail';
        state.match = hit;
      }
      renderResult();
    });

    renderResult();
    retick();
  }

  /* ------------------------------------------------------------------ map */
  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var LEAFLET_CSS_SRI = 'sha384-sHL9NAb7lN7rfvG5lfHpm643Xkcjzp4jFvuavGOndn6pjVqS6ny56CAt3nsEVT4H';
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var LEAFLET_JS_SRI = 'sha384-cxOPjt7s7Iz04uaHJceBmS+qpjv2JkIHNVcuOrM+YHwZOmJGBXI00mdUXEq65HTH';

  /* Real locality coordinates for every place named in the coverage copy.
     The coverage shape is a hull over these SERVED LOCALITIES, not over
     postcode rectangles. Do not swap in postcode geometry. */
  var PLACES = [
    ['Kuala Lumpur', 3.1466, 101.6958, 1], ['Ampang', 3.1500, 101.7600, 0],
    ['Cheras', 3.0800, 101.7450, 0], ['Setapak', 3.1980, 101.7180, 0],
    ['Bukit Jalil', 3.0580, 101.6900, 0], ['Petaling Jaya', 3.1073, 101.6067, 1],
    ['Subang Jaya', 3.0567, 101.5851, 1], ['Sunway', 3.0730, 101.6070, 0],
    ['Puchong', 3.0210, 101.6180, 0], ['Kota Damansara', 3.1520, 101.5920, 0],
    ['Bandar Utama', 3.1310, 101.6120, 0], ['Ara Damansara', 3.1120, 101.5720, 0],
    ['Sungai Buloh', 3.2060, 101.5800, 0], ['Kelana Jaya', 3.1120, 101.5960, 0],
    ['Seri Kembangan', 3.0330, 101.7050, 0], ['Serdang', 3.0230, 101.7130, 0],
    ['Shah Alam', 3.0733, 101.5185, 1], ['Setia Alam', 3.1030, 101.4560, 0],
    ['Bukit Raja', 3.0790, 101.4600, 0], ['Kota Kemuning', 3.0080, 101.5250, 0],
    ['Bandar Puncak Alam', 3.2080, 101.4230, 0], ['Kapar', 3.1170, 101.3670, 0],
    ['Klang', 3.0449, 101.4455, 1], ['Port Klang', 3.0000, 101.3930, 0],
    ['Telok Panglima Garang', 2.9520, 101.4990, 0], ['Jenjarom', 2.9210, 101.5100, 0],
    ['Banting', 2.8170, 101.5010, 0], ['Kajang', 2.9930, 101.7880, 1],
    ['Bandar Baru Bangi', 2.9400, 101.7700, 0], ['Semenyih', 2.9500, 101.8420, 0],
    ['Beranang', 2.8200, 101.8480, 0], ['Hulu Langat', 3.1300, 101.8180, 0],
    ['Rawang', 3.3200, 101.5770, 1], ['Serendah', 3.3700, 101.6000, 0],
    ['Selayang', 3.2480, 101.6510, 0], ['Batu Caves', 3.2379, 101.6840, 0],
    ['Gombak', 3.2300, 101.7000, 0], ['Putrajaya', 2.9264, 101.6964, 1],
    ['Cyberjaya', 2.9213, 101.6559, 0], ['Dengkil', 2.8600, 101.6800, 0],
    ['Sepang', 2.6900, 101.7500, 0], ['KLIA', 2.7456, 101.7099, 0]
  ];

  function hull(pts) {
    var p = pts.slice().sort(function (a, b) { return a[0] - b[0] || a[1] - b[1]; });
    function cross(o, a, b) { return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]); }
    var lower = [], upper = [], i;
    for (i = 0; i < p.length; i++) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p[i]) <= 0) lower.pop();
      lower.push(p[i]);
    }
    for (i = p.length - 1; i >= 0; i--) {
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p[i]) <= 0) upper.pop();
      upper.push(p[i]);
    }
    return lower.concat(upper.slice(1, -1));
  }

  function loadOnce(tag, attrs, key) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('[data-hbd-dep="' + key + '"]');
      if (existing) {
        if (existing.getAttribute('data-hbd-loaded') === '1') return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }
      var el = document.createElement(tag);
      Object.keys(attrs).forEach(function (k) { el.setAttribute(k, attrs[k]); });
      el.setAttribute('data-hbd-dep', key);
      el.addEventListener('load', function () { el.setAttribute('data-hbd-loaded', '1'); resolve(); });
      el.addEventListener('error', reject);
      document.head.appendChild(el);
    });
  }

  function drawMap(host) {
    var map = L.map(host, {
      /* Every interaction is off on purpose. This answers a glanceable
         question; pinch-zoom is the wrong pattern at 390px. */
      zoomControl: false, dragging: false, touchZoom: false, scrollWheelZoom: false,
      doubleClickZoom: false, boxZoom: false, keyboard: false, attributionControl: true
    });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', /* licence requirement - never remove */
      opacity: 0.5, maxZoom: 19
    }).addTo(map);

    var lnglat = PLACES.map(function (p) { return [p[2], p[1]]; });
    var ring = hull(lnglat).map(function (p) { return [p[1], p[0]]; });
    /* Hairline buffer only. At 1.10 the shade reached Bestari Jaya, Kerling,
       Rasa and Kuala Kubu Baru, which are NOT served. */
    var cy = ring.reduce(function (s, p) { return s + p[0]; }, 0) / ring.length;
    var cx = ring.reduce(function (s, p) { return s + p[1]; }, 0) / ring.length;
    ring = ring.map(function (p) { return [cy + (p[0] - cy) * 1.015, cx + (p[1] - cx) * 1.015]; });

    var zone = L.polygon(ring, {
      color: '#E1006B', weight: 2.5, opacity: 0.95, fillColor: '#FF3D97', fillOpacity: 0.20,
      smoothFactor: 1, dashArray: null, interactive: false
    }).addTo(map);

    PLACES.forEach(function (p) {
      var major = p[3] === 1;
      L.circleMarker([p[1], p[2]], {
        radius: major ? 4.5 : 2.6, color: '#fff', weight: major ? 1.6 : 1,
        fillColor: major ? '#E1006B' : '#FF7A2F', fillOpacity: 1, interactive: false
      }).addTo(map);
    });

    var labelLayer = L.layerGroup().addTo(map);

    /* Labels are placed greedily by priority and skipped when their box would
       collide with one already placed, so names never overprint. Below 560px
       only the eight major localities are labelled; the rest stay as dots. */
    function drawLabels() {
      labelLayer.clearLayers();
      var narrow = window.innerWidth < 560;
      var order = PLACES.slice().sort(function (a, b) { return b[3] - a[3]; });
      var boxes = [];
      order.forEach(function (p) {
        var major = p[3] === 1;
        if (narrow && !major) return;
        var pt = map.latLngToContainerPoint([p[1], p[2]]);
        var w = p[0].length * (major ? 7.2 : 6.2) + 8, h = major ? 16 : 14;
        var box = [pt.x + 6, pt.y - (major ? 7 : 5) - h / 2, pt.x + 6 + w, pt.y - (major ? 7 : 5) + h / 2];
        var clash = boxes.some(function (b) {
          return !(box[2] < b[0] || box[0] > b[2] || box[3] < b[1] || box[1] > b[3]);
        });
        if (clash) return;
        boxes.push(box);
        L.marker([p[1], p[2]], {
          interactive: false,
          icon: L.divIcon({
            className: '',
            html: '<div class="hbd-maplabel' + (major ? ' hbd-maplabel--major' : '') + '">' + p[0] + '</div>',
            iconSize: [0, 0], iconAnchor: [-6, major ? 7 : 5]
          })
        }).addTo(labelLayer);
      });
    }

    function fit() { map.fitBounds(zone.getBounds(), { padding: [26, 26] }); drawLabels(); }
    fit();
    map.on('zoomend moveend', drawLabels);
    window.addEventListener('resize', fit);
  }

  function initMap(host) {
    var started = false;

    function go() {
      if (started) return;
      started = true;
      Promise.all([
        loadOnce('link', { rel: 'stylesheet', href: LEAFLET_CSS, integrity: LEAFLET_CSS_SRI, crossorigin: 'anonymous' }, 'leaflet-css'),
        loadOnce('script', { src: LEAFLET_JS, integrity: LEAFLET_JS_SRI, crossorigin: 'anonymous' }, 'leaflet-js')
      ]).then(function () {
        drawMap(host);
      }).catch(function () {
        /* Tiles or library blocked: drop the frame rather than leave a hole.
           The postcode checker above is the definitive answer anyway. */
        var frame = host.parentNode;
        while (frame && !frame.hasAttribute('data-hbd-mapframe')) frame = frame.parentNode;
        if (frame) frame.setAttribute('hidden', 'hidden');
      });
    }

    if (!('IntersectionObserver' in window)) return go();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { io.disconnect(); go(); } });
    }, { rootMargin: '200px' });
    io.observe(host);
  }

  /* ----------------------------------------------------------------- boot */
  function boot() {
    var root = document.querySelector('[data-hbd-root]');
    if (!root) return;
    loadCoverage();
    initChecker(root);
    var mapHost = root.querySelector('[data-hbd-map]');
    if (mapHost) initMap(mapHost);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
